'use client';

/**
 * Shared presentational bits for the Kerno auth screens, ported from the
 * Claude Design bundle (auth-components.jsx). Visual only — no app logic.
 */
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Link from 'next/link';

/* ---- Inline glyphs (1.6px stroke, currentColor) ---- */
type GlyphName =
  | 'arrow-right'
  | 'arrow-left'
  | 'mail'
  | 'eye'
  | 'eye-off'
  | 'fingerprint'
  | 'alert';

export function Glyph({ name, size = 16 }: { name: GlyphName; size?: number }) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'arrow-right':
      return (
        <svg {...props}>
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      );
    case 'arrow-left':
      return (
        <svg {...props}>
          <path d="M19 12H5" />
          <path d="m12 19-7-7 7-7" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3 7 9 6 9-6" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...props}>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'eye-off':
      return (
        <svg {...props}>
          <path d="m3 3 18 18" />
          <path d="M10.6 6.1A9.7 9.7 0 0 1 12 6c6.5 0 10 6 10 6a16 16 0 0 1-3.5 4.4M6.4 7.5A16 16 0 0 0 2 12s3.5 6 10 6c1.5 0 2.8-.3 4-.8" />
          <path d="M9.5 9.5a3.5 3.5 0 0 0 5 5" />
        </svg>
      );
    case 'fingerprint':
      return (
        <svg {...props}>
          <path d="M12 2c-3.3 0-6 1.7-7.5 4.2" />
          <path d="M3 13c0-1.7.4-3.3 1.2-4.7" />
          <path d="M21 13c0-2.5-1-4.7-2.6-6.4" />
          <path d="M16 16c0-2.2-1.8-4-4-4s-4 1.8-4 4c0 1.5.5 3 1.4 4.4" />
          <path d="M12 8a5 5 0 0 1 5 5c0 2.4-.6 4.6-1.7 6.5" />
          <path d="M7 13c0-1.5.6-2.9 1.6-4" />
          <path d="M12 16v3" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4" />
          <circle cx="12" cy="16" r=".75" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
}

/* ---- OAuth provider brand marks ---- */
export function OAuthGlyph({ provider }: { provider: 'google' | 'apple' }) {
  if (provider === 'google') {
    return (
      <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.3c1.9-1.8 3-4.4 3-7.3Z"
        />
        <path
          fill="#34A853"
          d="M12 22c2.7 0 5-.9 6.7-2.5l-3.3-2.5c-.9.6-2.1 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3v2.6A10 10 0 0 0 12 22Z"
        />
        <path fill="#FBBC05" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3a10 10 0 0 0 0 9l3.4-2.6Z" />
        <path
          fill="#EA4335"
          d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9C17 2.9 14.7 2 12 2A10 10 0 0 0 3 7.5l3.4 2.6C7.2 7.6 9.4 5.9 12 5.9Z"
        />
      </svg>
    );
  }
  // Apple — monochrome mark, inherits text colour
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.9-3.5.9-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.7 2.4 3 2.3 1.2 0 1.6-.8 3.1-.8 1.4 0 1.9.8 3.1.8 1.3 0 2.1-1.2 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.6-1-2.7-3.9ZM14.2 5.4c.7-.8 1.1-2 1-3.1-1 0-2.1.7-2.8 1.5-.6.7-1.1 1.9-1 2.9 1 .1 2.1-.5 2.8-1.3Z"
      />
    </svg>
  );
}

/* ---- Form shell: Kerno chrome (brand header, card, footer) ---- */
export function FormShell({ headRight, children }: { headRight?: ReactNode; children: ReactNode }) {
  const year = 2026;
  return (
    <div className="auth-form-col">
      <header className="auth-form-head">
        <Link className="auth-brand" href="/" aria-label="Kerno home">
          <div className="auth-brand-mark">K</div>
          <div className="auth-brand-name">
            Kerno<span className="brand-tag">erp</span>
          </div>
        </Link>
        <div className="auth-form-meta">{headRight}</div>
      </header>
      <main className="auth-form-body">
        <div className="auth-card">{children}</div>
      </main>
      <footer className="auth-form-foot">
        <div>© {year} Kerno</div>
        <div>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </div>
      </footer>
    </div>
  );
}

/* ---- Password strength ---- */
export type Strength = { tier: '' | 'weak' | 'fair' | 'strong' | 'excellent'; label: string };
export function strength(pw: string): Strength {
  if (!pw) return { tier: '', label: '' };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { tier: 'weak', label: 'Weak' };
  if (s === 2) return { tier: 'fair', label: 'Fair' };
  if (s === 3) return { tier: 'strong', label: 'Strong' };
  return { tier: 'excellent', label: 'Excellent' };
}

/* ---- Last-used method memory ---- */
const LAST_USED_KEY = 'kerno.auth.lastUsed';
type LastUsedMethod = 'google' | 'apple' | 'email' | 'passkey' | 'magic';
export function useLastUsed(): [LastUsedMethod | null, (m: LastUsedMethod) => void] {
  // Start null so server + first client render match; hydrate from storage after mount.
  const [lu, setLu] = useState<LastUsedMethod | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_USED_KEY);
      if (raw) setLu(JSON.parse(raw).method as LastUsedMethod);
    } catch {
      /* ignore */
    }
  }, []);
  const mark = (method: LastUsedMethod) => {
    try {
      localStorage.setItem(LAST_USED_KEY, JSON.stringify({ method, at: Date.now() }));
    } catch {
      /* ignore */
    }
    setLu(method);
  };
  return [lu, mark];
}
